import { OfferType } from '@occc-app/offerValidationService/types';
import {
  OfferFragment,
  Earning_Mechanism_Product_Counting_Attribute,
  Constraint_Purchase_Threshold_Counting_Attribute,
  Offer_Engagement_Type,
  ProductGroupFragment,
} from '@occc/graphql';
import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import { ZodValidationError } from '@occc/ui-forms';
import { getAwardCountInitialValues, LimitTimePeriod } from '@occc/feat-limits';
import {
  getMinimumPurchaseConstraintInitialValues,
  getPaymentMethodInitialValues,
  getRequiresActivationInitialValues,
} from '@occc/feat-constraints';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { getProductChallengeFormValues } from '@occc-app/routes/Challenge/components/ChallengeConfigureForm/utils/configureFormInitialValues';
import {
  getProductPurchaseFormValues,
  getSpendBonusFormValues,
} from '@occc-app/offerHelpers/utils/transformOfferEarningMechanismToInitialValues';
import { GetTargetTabProps } from '@occc-app/offerHelpers/TargetForm/types';

import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import { getOfferFulfillmentPriorityInitialValues } from '@occc-app/offerHelpers/utils/getOfferFulfillmentPriorityInitialValues';
import { transformOfferScanDataConfigurationToInitialValues } from '@occc-app/offerHelpers/utils/transformOfferScanDataConfigurationToInitialValues';
import {
  ProductPurchaseFormSchema,
  RewardFormSchema,
  SpendBonusFormSchema,
  VarietyChallengeFormSchema,
} from './schemas';
import {
  SweepstakesAwardVouchersFormValues,
  SweepstakesConfigureFormInitialValues,
  SweepstakesConfigureFormValues,
  SweepstakesEarningMechanismInitialValues,
  SweepstakesType,
} from './types';

import { createRouteCreator } from '../CampaignComponents/utils';
import { transformCustomData } from '../CampaignComponents/components/CustomData/utils';

export const routeCreator = createRouteCreator(OfferType.Sweepstakes);

const validatorBySweepstakesType: Record<
  SweepstakesType,
  (value: unknown) => Promise<ZodValidationError>
> = {
  [SweepstakesType.ProductPurchase]: makeValidate(ProductPurchaseFormSchema),
  [SweepstakesType.VarietyChallenge]: makeValidate(VarietyChallengeFormSchema),
  [SweepstakesType.SpendBonus]: makeValidate(SpendBonusFormSchema),
  [SweepstakesType.Reward]: makeValidate(RewardFormSchema),
};

export const validateConfiguration = async (values: SweepstakesConfigureFormValues) => {
  if (values.sweepstakesType == null) {
    return;
  }

  return validatorBySweepstakesType[values.sweepstakesType](values);
};

const getConfigureFormInitialValues = (): SweepstakesConfigureFormInitialValues =>
  ({
    sweepstakesType: null,
    earningMechanism: {},
    productChallenge: {
      products: [{ countProductsBy: Earning_Mechanism_Product_Counting_Attribute.Quantity }],
      earningMechanism: 'varietyPurchase',
    },
    awardVouchers: {
      isEnabled: false,
      voucherCount: 1,
    },
    awardCount: {
      global: {
        isEnabled: false,
        timePeriod: LimitTimePeriod.Total,
      },
      membership: {
        isEnabled: false,
        timePeriod: LimitTimePeriod.Total,
      },
    },
    activation: {
      isEnabled: true,
      requiresActivation: {
        isMustFulfillWithinEnabled: false,
        allowMultipleFulfillmentsOnActivation: true,
      },
    },
    paymentMethodConstraint: {
      isEnabled: false,
    },
    minimumPurchaseConstraints: {
      isEnabled: false,
      items: [
        {
          countProductsBy: Constraint_Purchase_Threshold_Counting_Attribute.Quantity,
        },
      ],
    },
    scanDataConfiguration: {
      isEnabled: false,
      fundingSource: '',
      description: '',
      discountAllocation: undefined,
    },
  } as SweepstakesConfigureFormInitialValues);

const getAwardVouchers = (
  award: Parameters<typeof awardHelper.isVouchersAward>[0],
): SweepstakesAwardVouchersFormValues | undefined => {
  if (!awardHelper.isVouchersAward(award)) {
    return undefined;
  }

  const { voucheredOffer, voucherExpirationDays, voucherCount } = award;

  return voucherExpirationDays
    ? { voucheredOffer, voucherCount, voucherExpirationDays, isEnabled: true }
    : { voucheredOffer, voucherCount, isEnabled: false };
};

const transformEarningMechanism = (
  earningMechanism: OfferFragment['earningMechanism'],
): SweepstakesEarningMechanismInitialValues => {
  if (!earningMechanism) {
    return {};
  }

  if (earningMechanismHelper.isPointSpend(earningMechanism)) {
    return {
      sweepstakesType: SweepstakesType.Reward,
      pointSpend: { pointCost: earningMechanism.pointCost },
      awardVouchers: getAwardVouchers(earningMechanism.pointSpendAward),
    };
  }

  if (earningMechanismHelper.isProductPurchase(earningMechanism)) {
    return {
      sweepstakesType: SweepstakesType.ProductPurchase,
      productPurchase: getProductPurchaseFormValues(earningMechanism),
      awardVouchers: getAwardVouchers(earningMechanism.productPurchaseAward),
    };
  }

  if (earningMechanismHelper.isTotalSpendOverTime(earningMechanism)) {
    return {
      sweepstakesType: SweepstakesType.SpendBonus,
      spendBonus: getSpendBonusFormValues(earningMechanism),
      awardVouchers: getAwardVouchers(earningMechanism.totalSpendOverTimeAward),
    };
  }

  if (earningMechanismHelper.isVarietyPurchase(earningMechanism)) {
    return {
      sweepstakesType: SweepstakesType.VarietyChallenge,
      productChallenge: getProductChallengeFormValues(earningMechanism, 'varietyPurchase'),
      awardVouchers: getAwardVouchers(earningMechanism.varietyPurchaseAward),
    };
  }

  if (earningMechanismHelper.isVarietyPurchaseOverTime(earningMechanism)) {
    return {
      sweepstakesType: SweepstakesType.VarietyChallenge,
      productChallenge: getProductChallengeFormValues(earningMechanism, 'varietyPurchaseOverTime'),
      awardVouchers: getAwardVouchers(earningMechanism.varietyPurchaseOverTimeAward),
    };
  }

  return {};
};

const getAwardCountSchemaInitialValues = (
  offer: OfferFragment,
  defaultValues: SweepstakesConfigureFormInitialValues['awardCount'],
): NonNullable<SweepstakesConfigureFormInitialValues['awardCount']> =>
  getAwardCountInitialValues(offer, defaultValues) as NonNullable<
    SweepstakesConfigureFormInitialValues['awardCount']
  >;

export const transformToInitialValues = (
  offer?: OfferFragment,
  productGroups?: ProductGroupFragment[],
): SweepstakesConfigureFormInitialValues => {
  const values = getConfigureFormInitialValues();

  if (!offer) {
    return values;
  }

  return {
    ...values,
    ...getOfferNameAndDescriptionInitialValues(offer),
    ...getOfferFulfillmentPriorityInitialValues(offer),
    customData: transformCustomData(offer.customData),
    earningMechanism: {
      id: offer.earningMechanism?.id,
    },
    awardCount: getAwardCountSchemaInitialValues(offer, values.awardCount),
    activation: getRequiresActivationInitialValues(offer, values.activation),
    ...transformEarningMechanism(offer.earningMechanism),
    paymentMethodConstraint: getPaymentMethodInitialValues(offer),
    ...getMinimumPurchaseConstraintInitialValues(offer, productGroups),
    ...transformOfferScanDataConfigurationToInitialValues(offer.scanDataConfiguration),
  };
};

export const getTargetTabProps: GetTargetTabProps = (offer) => {
  const isSweepstakesReward =
    offer?.engagementType === Offer_Engagement_Type.SweepstakesPurchasable;

  return {
    shouldShowStoreLocationOrGroups: !isSweepstakesReward,
  };
};
